import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import DeleteIcon from '@mui/icons-material/Delete';
import CubeIcon from '@svg/cube';

import { v4 as uuidv4 } from 'uuid';
import { Table } from '@common/Table';
import { AppDispatch, FlowModals, State, flowActions, showTelnyxFeatures } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@common/Button';
import { Alert, Chip, IconButton, Stack } from '@mui/material';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid';
import { Flow } from '@store/async';
import { LayoutWorkspace } from '@common/layout';
import { tabActions } from '@store/slices/tabs';
import { AddToPhotosOutlined, DeviceHubOutlined, FileUploadOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listWorkflows, saveWorkflow } from '@requests/flow';
import { useSnackbar } from 'notistack';
import { Translation, TranslationNode } from '@components/Flow';
import { Resource } from '@store/types';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { ErrorContainer } from '../Buckets';
import { ResourceFallback } from '@common/ResourceFallback';
import { usePermissions } from '@common/hooks/usePermissions';
import { useTheme } from '../../../../styles';
import { updateIntercomWithWorkflows } from '@common/utils/intercom';
import { RequestError } from '@requests/common';
import { ViewModuleWorkflows } from '@components/Modals/ViewModuleWorkflows/ViewModuleWorkflow';

const Container = styled.div`
  display: flex;
`;

interface Props {
  type: 'workflow' | 'module';
}

export type ModuleWorkflowsAssigned = {
  flow_id: string;
  name: string;
  node_count: number;
};

export const Workflows: React.FC<Props> = ({ type }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();

  const { service_id } = useParams<{ service_id: string }>();
  const { admin, permissions } = usePermissions(service_id as string, Resource.WORKFLOW);

  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));
  const loading = useSelector((state: State) => state.flow.flowLoading);
  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);

  const allowCreate = admin || permissions?.create;
  const allowDelete = admin || permissions?.delete;

  const {
    data: workflows = [],
    isLoading: isWorkflowLoading,
    refetch: refetchWorkflows,
    isFetchedAfterMount,
    error,
  } = useQuery<Flow[], RequestError, Flow[], [string, string | undefined]>({
    queryKey: ['workflows', service_id],
    queryFn: listWorkflows,
    retry: false,
    enabled: Boolean(service_id),
  });

  useEffect(() => {
    if (!isFetchedAfterMount) return;
    updateIntercomWithWorkflows(workflows);
  }, [isFetchedAfterMount]);

  const errorMessage = error?.response?.data?.error || error?.message;

  const viewModuleWorkflows = (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>,
    workflowsAssigned: ModuleWorkflowsAssigned[]
  ) => {
    event.stopPropagation();
    dispatch(flowActions.setViewModuleWorkflows(workflowsAssigned));
  };

  const columns = [
    {
      field: 'flow_id',
      headerName: 'ID',
      flex: 100,
      sortable: true,
      defaultVisible: false,
    },
    {
      field: 'name',
      headerName: type === 'workflow' ? 'Workflow' : 'Module',
      flex: type === 'workflow' ? 100 : 300,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Flow>) => {
        const { row } = params;

        return (
          <Stack direction='row' spacing={1.5} alignItems='center'>
            {row.trigger_type !== 'workflow.module' ? (
              <DeviceHubOutlined sx={{ color: theme.text.subtext, fontSize: '1.8ch !important' }} />
            ) : (
              <CubeIcon color={theme.text.subtext} height={16} />
            )}
            <div>{row.name}</div>
          </Stack>
        );
      },
    },
    type === 'workflow' && {
      field: 'service_path',
      headerName: 'Endpoint',
      flex: 220,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Flow>) => {
        const row = params.row;

        return <pre>{`/execute/${row.service_id}/${row.service_path}`}</pre>;
      },
    },
    type === 'workflow' &&
      isTelnyxUser && {
        field: 'public',
        headerName: 'Live',
        flex: 50,
        sortable: true,
        renderCell: (params: GridRenderCellParams<Flow>) => {
          const { row } = params;

          return (
            <Chip
              variant={row.public ? 'filled' : 'outlined'}
              size='small'
              label={row.public ? 'Public' : 'Internal'}
              color={row.public ? 'primary' : undefined}
            />
          );
        },
      },
    {
      field: 'created_by',
      headerName: 'Created By',
      flex: 50,
      sortable: true,
    },
    type === 'module' && {
      field: 'module_used_in_workflows',
      headerName: 'Used In Workflows',
      flex: 70,
      type: 'array',
      sortable: true,
      renderCell: (params: GridRenderCellParams<Flow>) => {
        const row = (params.formattedValue || []) as ModuleWorkflowsAssigned[];

        return (
          <Stack direction='row' spacing={1} alignItems='center'>
            <Chip
              size='small'
              sx={{ width: '100px' }}
              label={`${row.length} Workflows`}
              disabled={row.length === 0}
              onClick={(e) => viewModuleWorkflows(e, row)}
            />
            <IconButton size='small' disabled={row.length === 0} onClick={(e) => viewModuleWorkflows(e, row)}>
              <VisibilityOutlined fontSize='small' />
            </IconButton>
          </Stack>
        );
      },
    },
    {
      field: 'updated_by',
      headerName: 'Last Updated',
      flex: 50,
      sortable: true,
      defaultVisible: false,
    },
    {
      field: 'node_count',
      headerName: 'Nodes',
      flex: 30,
      sortable: true,
    },
    {
      field: 'created_at',
      headerName: 'Created At (UTC)',
      flex: 65,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Flow>) => {
        const { row } = params;

        return <div>{new Date(row.updated_at).toLocaleString()}</div>;
      },
    },
    {
      field: 'updated_at',
      headerName: 'Updated At (UTC)',
      flex: 65,
      sortable: true,
      defaultVisible: false,
      renderCell: (params: GridRenderCellParams<Flow>) => {
        const { row } = params;

        return <div>{new Date(row.updated_at).toLocaleString()}</div>;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: string }) => {
        return [
          <GridActionsCellItem
            key='delete'
            icon={<DeleteIcon />}
            label='Delete'
            disabled={!allowDelete}
            onClick={() => {
              dispatch(flowActions.setDeleteTab({ id, returnPath: type === 'module' ? 'modules' : 'workflow' }));
            }}
            color='inherit'
          />,
        ];
      },
    },
  ].filter(Boolean);

  const createFlow = () => {
    const flow_id = uuidv4();
    dispatch(tabActions.createNewTab(flow_id));
    dispatch(flowActions.reset());
    dispatch(
      flowActions.setFlowModal({
        type: FlowModals.SelectNewFlow,
        variant: type,
      })
    );
    navigate(`/${service_id}/workflow/${flow_id}`);
  };

  const parsedWorkflows = Array.isArray(workflows) ? workflows : [];
  const filteredWorkflows = parsedWorkflows.filter(
    (workflow: Flow) =>
      (type === 'workflow' && workflow.trigger_type !== 'workflow.module') ||
      (type === 'module' && workflow.trigger_type === 'workflow.module')
  );

  const isEmptyWorkspaces = filteredWorkflows?.length === 0 && !isWorkflowLoading;

  const empty = (
    <Container>
      <ServiceNavigation />
      <ResourceFallback
        resourceName={type === 'workflow' ? 'Workflow' : 'Module'}
        resourceIcon={type == 'workflow' ? <AddToPhotosOutlined /> : <CubeIcon />}
        resourceInfo={
          type === 'workflow'
            ? 'A workflow is a collection of nodes and edges, triggered by a HTTP request. Each workflow represents a different set of actions to be executed.'
            : 'A module is a collection of nodes and edges, similar to a workflow. A module can only be executed as a node in a workflow. You can see your list of module nodes by right clicking the canvas in a workflow. Use modules to encapsulate reusable logic across workflows.'
        }
        onClickCreate={createFlow}
        onClickGuide={() =>
          type === 'workflow' ? navigate(`/documentation/endpoints`) : navigate(`/documentation/module`)
        }
        createDisabled={!allowCreate}
      />
    </Container>
  );

  const body = error ? (
    <Container>
      <ServiceNavigation />
      <ErrorContainer>
        <Alert
          severity='error'
          action={
            <Button onClick={() => refetchWorkflows()} color='inherit' size='small'>
              Retry
            </Button>
          }
        >
          {errorMessage || 'An unexpected error occured whilst trying to load your workflows. Please try again.'}
        </Alert>
      </ErrorContainer>
    </Container>
  ) : (
    <Container>
      <ServiceNavigation />
      <Table
        // page width - navbar - sidebar - sidebar padding
        width='calc(100vw - 61px - 240px - 48px)'
        height='calc(100vh - 61px)'
        data={filteredWorkflows}
        sortModel='public'
        columns={columns}
        density={'standard'}
        getRowId={(row) => row.flow_id}
        loading={loading || isWorkflowLoading}
        initialState={{
          columns: {
            columnVisibilityModel: {
              flow_id: false,
              updated_by: false,
              updated_at: false,
            },
          },
        }}
        onRowClick={({ row }) => {
          dispatch(tabActions.setIdentifier(row.flow_id));
          navigate(`/${service_id}/workflow/${row.flow_id}`);
        }}
      />
      <ViewModuleWorkflows />
    </Container>
  );

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={isEmptyWorkspaces && !error ? empty : body}
      actions={
        <Stack direction='row' spacing={1.5}>
          {isTelnyxUser && <FileUploadButton disabled={!allowCreate} />}
          {!isEmptyWorkspaces && (
            <Button
              customColor='black'
              onClick={createFlow}
              startIcon={type === 'workflow' ? <AddToPhotosOutlined /> : <CubeIcon height={15} />}
              disabled={!allowCreate}
              data-intercom-target='workflow-create-button'
            >
              {type === 'workflow' ? 'Add Workflow' : 'Add Module'}
            </Button>
          )}
        </Stack>
      }
    />
  );
};

interface FileUploadButtonProps {
  disabled?: boolean;
}
const FileUploadButton = ({ disabled }: FileUploadButtonProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const queryClient = useQueryClient();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const username = useSelector((state: State) => state.auth.username);
  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);

  const { mutateAsync: save } = useMutation({
    mutationFn: saveWorkflow,
    onSuccess: (data) => {
      if (!data) return;
      if (data?.data?.error) {
        enqueueSnackbar(data?.data?.error?.error || data?.error, {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to import workflow!', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        dispatch(tabActions.setSavedById({ id: data.flow_id, saved: false }));

        return;
      } else enqueueSnackbar('Workflow imported successfully!', { variant: 'success', autoHideDuration: 3000 });

      dispatch(tabActions.setSavedById({ id: data.flow_id, saved: true }));
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['flow_info'] });
    },
  });

  function isValidTranslation(obj: any): obj is Translation {
    // Check flow_id is either string or null
    if (typeof obj.flow_id !== 'string' && obj.flow_id !== null) return false;

    // Check name is a string
    if (typeof obj.name !== 'string') return false;

    // Check edges is an array with the correct structure
    if (
      !Array.isArray(obj.edges) ||
      !obj.edges.every(
        (edge: any) =>
          typeof edge.source === 'string' &&
          typeof edge.target === 'string' &&
          typeof edge.sourceHandle === 'string' &&
          typeof edge.targetHandle === 'string' &&
          typeof edge.variant === 'string'
      )
    ) {
      return false;
    }

    // Assuming you have a function to validate each node, if you have a specific structure for TranslationNode
    if (!Array.isArray(obj.nodes) || !obj.nodes.every(isValidTranslationNode)) {
      return false;
    }

    return true;
  }

  function isValidTranslationNode(node: any): node is TranslationNode {
    // Perform checks similar to isValidTranslation to validate each node
    // This is just a placeholder; you'll need to replace it with actual checks
    return true;
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const input = event?.target?.result;

        if (typeof input === 'string') {
          const json = JSON.parse(input);

          const name = `${json.name} (Import)`;
          const nodes = json.nodes;
          const flow_id = uuidv4();
          const edges = json.edges;
          const config = { name, flow_id, nodes, edges };
          const data_prefixes = json?.flow?.data_prefixes || {};

          const trigger = nodes.find((node: any) => node?.trigger);

          if (!trigger?.command_id) throw new Error('No trigger found in the flow');
          if (!isValidTranslation(config)) throw new Error('Invalid input schema detected');

          const id = uuidv4();

          save({
            config,
            username: username || '',
            service_id: service_id as string,
            service_path: id,
            trigger_type: trigger?.command_id as string,
            authentication,
            data_prefixes,
          });
        }
      } catch (error) {
        console.error('Error reading JSON:', error);
        if (error instanceof Error) {
          enqueueSnackbar(`Error Message: ${error?.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        } else {
          enqueueSnackbar('Error Message: There was an unexpected configuration issue with the input file.', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }

        enqueueSnackbar('Failed to import workflow!', {
          variant: 'error',
          autoHideDuration: 10000,
        });
      }
    };

    reader.readAsText(acceptedFiles[0]);
  }, []);

  const { getInputProps, open } = useDropzone({
    onDrop: onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: { 'application/json': ['.json'] },
  });

  return (
    <div>
      <input {...getInputProps()} />
      <Button
        onClick={open}
        startIcon={<FileUploadOutlined sx={{ fontSize: '2.4ch !important' }} />}
        disabled={disabled}
      >
        Import
      </Button>
    </div>
  );
};
